import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden flex-1 bg-card lg:block" style={{ backgroundImage: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.06), transparent 60%)" }}>
        <div className="flex h-full flex-col justify-between p-12">
          <img src="/logo.png" alt="Fit House Drinks & Coffee" data-testid="login-hero-logo"
            className="w-[420px] max-w-full self-center object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] lg:mt-16" />
          <div>
          <h2 className="font-heading text-4xl font-extrabold text-white">Taze. Fit. Ölçülebilir.</h2>
          <p className="mt-3 max-w-md text-white/80">
            Malzemeden ürüne, makro hesabından kâr-zarara kadar kafenizi tek panelden yönetin.
          </p>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-center p-6 lg:w-[480px]">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-3">
            <img src="/logo.png" alt="Fit House" data-testid="login-logo" className="h-16 w-16 object-contain" />
            <div>
              <p className="font-heading text-xl font-extrabold tracking-tight">Fit House</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Drinks & Coffee · Yönetim Paneli</p>
            </div>
          </div>

          <h1 className="font-heading text-2xl font-bold">Tekrar hoş geldiniz 👋</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">Devam etmek için giriş yapın.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full font-semibold">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Giriş Yap
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> veya <span className="h-px flex-1 bg-border" />
          </div>
          <Button
            type="button"
            variant="secondary"
            data-testid="google-login-btn"
            className="w-full font-semibold"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Google ile giriş yap
          </Button>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-link">
              Şifrenizi mi unuttunuz?
            </Link>
            <Link to="/register" className="text-muted-foreground hover:text-primary hover:underline" data-testid="register-link">
              Hesap oluştur
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
